import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ServiceRollingPoster = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const posters = [
    {
      id: 'fullstack',
      tabLabel: 'Full-Scale Web',
      tabIcon: '💻',
      pill: 'FULL-SCALE ENGINEERING',
      title: 'Full-Scale Web & SaaS Development',
      tagline: 'Modern Frontend, Resilient Backends & Distributed Cloud Systems',
      desc: 'We architect enterprise-grade digital platforms from the database schema up to pixel-perfect reactive interfaces. Engineered for high concurrency, zero technical debt, and extreme performance.',
      previewImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
      imageCaption: 'Full-Stack Distributed Architecture & Reactive Web',
      features: [
        'Reactive Frontend: React, TypeScript, Tailwind, Next.js',
        'Scalable Backends: Node.js, Python, Go, GraphQL & REST',
        'Database Mastery: PostgreSQL, Redis caching, schema migrations',
        'Cloud-Native: Containerized Docker deployments, AWS & GCP'
      ],
      ctaText: 'Scope Web & SaaS Build',
      ctaLink: '/rfp',
      badgeColor: '#1d4ed8',
      visualType: 'code_matrix'
    },
    {
      id: 'agentic_ai',
      tabLabel: 'Agentic AI',
      tabIcon: '🤖',
      pill: 'AUTONOMOUS SYSTEMS',
      title: 'Cloud AI & Agentic Solutions',
      tagline: 'Autonomous AI Agents, Multi-Agent Workflows & Cognitive Pipelines',
      desc: 'Move beyond basic prompt wrappers. We engineer autonomous multi-agent architectures where specialized AI agents plan, execute, verify, self-correct, and invoke custom APIs to automate complex business workflows.',
      previewImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=800&q=80',
      imageCaption: 'Multi-Agent Autonomous Cognitive Pipelines',
      features: [
        'Multi-Agent Coordination: Hierarchical planning & verification loops',
        'Custom Tooling: API calling, browser automation & database querying',
        'Enterprise RAG: Hybrid semantic search with grounded vector DBs',
        'Zero-Hallucination Guardrails: Strict output validation & schema enforcement'
      ],
      ctaText: 'Build Custom Agentic Solution',
      ctaLink: '/rfp',
      badgeColor: '#059669',
      visualType: 'agentic_graph'
    },
    {
      id: 'chatbots',
      tabLabel: 'AI Chatbots',
      tabIcon: '💬',
      pill: 'CONVERSATIONAL INTELLIGENCE',
      title: 'Enterprise Chatbots & Support AI',
      tagline: 'Domain-Trained Conversational Intelligence with Real-Time Context',
      desc: 'Deploy high-IQ customer support and internal team assistants trained on your proprietary documentation, CRM, and product data. Secure, compliant, and integrated directly into your existing software stack.',
      previewImage: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80',
      imageCaption: 'Grounded Enterprise Conversational Agents',
      features: [
        'Context-Aware Knowledge Retrieval: Grounded on internal knowledge bases',
        'Multi-Channel Deployment: Web embed, Slack, Teams, WhatsApp & API',
        'Human-in-the-Loop: Seamless triage and escalation to live engineers',
        'Privacy First: SOC2 compliance, data sanitization & private VPC models'
      ],
      ctaText: 'Deploy Enterprise Chatbot',
      ctaLink: '/rfp',
      badgeColor: '#7c3aed',
      visualType: 'chat_ui'
    },
    {
      id: 'mobile',
      tabLabel: 'iOS & Android',
      tabIcon: '📱',
      pill: 'NATIVE & CROSS-PLATFORM',
      title: 'iOS & Android Mobile Applications',
      tagline: '60 FPS Native Polish, Offline-First Sync & App Store Success',
      desc: 'From native Swift iOS architectures to robust cross-platform Android solutions. We build fluid mobile applications with offline-first synchronization, biometric security, and intuitive mobile UX.',
      previewImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80',
      imageCaption: 'Native iOS & Cross-Platform Mobile Engineering',
      features: [
        'iOS Native: Swift, SwiftUI, CoreData & Apple ecosystem integrations',
        'Android & Cross-Platform: React Native, Kotlin, smooth gesture physics',
        'Offline Sync: Local database caching with automatic cloud reconciliation',
        'App Store Lifecycle: TestFlight, CI/CD automated deployment & compliance'
      ],
      ctaText: 'Develop Mobile App',
      ctaLink: '/rfp',
      badgeColor: '#0284c7',
      visualType: 'mobile_device'
    },
    {
      id: 'devops_maintenance',
      tabLabel: 'DevOps & 24/7 SLA',
      tabIcon: '🛡️',
      pill: '24/7 PLATFORM RELIABILITY',
      title: 'DevOps & 24/7 SLA Maintenance Retainers',
      tagline: 'Continuous Monitoring, Zero-Downtime Patching & Dedicated SRE Hours',
      desc: 'Software is a living system. Our structured monthly support retainers provide 24/7 automated uptime surveillance, rapid critical incident response, continuous security patching, and ongoing feature development.',
      previewImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80',
      imageCaption: '24/7 SRE Telemetry & Cloud Datacenter Management',
      features: [
        'Rapid Response SLAs: 1-Hour critical response windows available (defined in SOW)',
        'Automated CI/CD: Zero-downtime blue/green deployment pipelines',
        'Proactive Patching: Continuous CVE security scanning and dependency upgrades',
        'Dedicated SRE Hours: Monthly banked hours for feature enhancements & bugfixes'
      ],
      ctaText: 'Calculate Retainer Pricing',
      ctaLink: '/calculator',
      badgeColor: '#d97706',
      visualType: 'devops_cluster'
    }
  ];

  // Auto-rotation timer (6 seconds per poster, paused on hover)
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % posters.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPaused, posters.length]);

  const activePoster = posters[currentSlide];

  return (
    <section 
      style={{ 
        padding: '5rem 0 6rem 0', 
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        position: 'relative'
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container-custom">
        {/* Section Header */}
        <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 3rem auto' }}>
          <div className="pill-badge pill-blue" style={{ marginBottom: '1rem' }}>
            <span>🚀</span> FULL-SPECTRUM CAPABILITIES
          </div>
          <h2 style={{ fontSize: 'clamp(2rem, 3.8vw, 3rem)', fontWeight: 900, marginBottom: '1rem', letterSpacing: '-0.025em' }}>
            What We Build &amp; Support
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.12rem', lineHeight: '1.6' }}>
            From high-scale web platforms and autonomous AI agents to native mobile apps and 24/7 reliability retainers.
          </p>
        </div>

        {/* Rolling Poster Selector Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          marginBottom: '2.5rem'
        }}>
          {posters.map((poster, index) => {
            const isActive = currentSlide === index;
            return (
              <button
                key={poster.id}
                type="button"
                onClick={() => setCurrentSlide(index)}
                style={{
                  background: isActive ? '#0f172a' : '#f8fafc',
                  color: isActive ? '#ffffff' : '#475569',
                  border: `1px solid ${isActive ? '#0f172a' : '#e2e8f0'}`,
                  padding: '0.55rem 1.15rem',
                  borderRadius: '9999px',
                  fontSize: '0.84rem',
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  boxShadow: isActive ? '0 4px 12px rgba(15, 23, 42, 0.15)' : 'none'
                }}
              >
                <span>{poster.tabIcon}</span>
                <span>{poster.tabLabel}</span>
              </button>
            );
          })}
        </div>

        {/* The Rolling Poster Display Card */}
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '24px',
          padding: 'clamp(2rem, 4vw, 3.5rem)',
          boxShadow: '0 12px 36px -6px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(15, 23, 42, 0.04)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s ease'
        }}>
          {/* Animated Progress Line */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: '#e2e8f0'
          }}>
            <div 
              key={currentSlide}
              style={{
                height: '100%',
                backgroundColor: activePoster.badgeColor,
                animation: isPaused ? 'none' : 'progressBar 6s linear forwards'
              }}
            />
          </div>

          <div className="row align-items-center">
            {/* Left Content Side */}
            <div className="col-lg-6 mb-4 mb-lg-0 animate-fade-in" key={`content-${activePoster.id}`}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#ffffff',
                color: activePoster.badgeColor,
                border: '1px solid #e2e8f0',
                padding: '0.35rem 0.9rem',
                borderRadius: '9999px',
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                marginBottom: '1.2rem'
              }}>
                ● {activePoster.pill}
              </div>

              <h3 style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 900, lineHeight: 1.15, marginBottom: '0.8rem', color: '#0f172a' }}>
                {activePoster.title}
              </h3>

              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: activePoster.badgeColor, marginBottom: '1.2rem' }}>
                {activePoster.tagline}
              </div>

              <p style={{ color: '#475569', fontSize: '1.02rem', lineHeight: '1.65', marginBottom: '1.8rem' }}>
                {activePoster.desc}
              </p>

              <div style={{ marginBottom: '2.2rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.8rem' }}>
                  Architecture &amp; Core Deliverables:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {activePoster.features.map((feat, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.94rem', color: '#334155' }}>
                      <span style={{ color: activePoster.badgeColor, fontWeight: 900, fontSize: '1.1rem' }}>✓</span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <Link 
                  to={activePoster.ctaLink} 
                  className="btn-primary-blue"
                  style={{ backgroundColor: activePoster.badgeColor, borderColor: activePoster.badgeColor }}
                >
                  {activePoster.ctaText} →
                </Link>
                <Link to="/rfp" className="btn-outline-blue">
                  Submit Detailed RFP
                </Link>
              </div>
            </div>

            {/* Right Visual Side - Interactive Graphic Poster */}
            <div className="col-lg-6 animate-fade-in" key={`visual-${activePoster.id}`}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '18px',
                border: '1px solid #e2e8f0',
                padding: '1.5rem',
                boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.06)'
              }}>
                {/* Visual Photography Header */}
                {activePoster.previewImage && (
                  <div style={{ 
                    position: 'relative', 
                    borderRadius: '12px', 
                    overflow: 'hidden', 
                    marginBottom: '1.2rem', 
                    height: '145px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <img 
                      src={activePoster.previewImage} 
                      alt={activePoster.imageCaption}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '0.45rem 0.85rem',
                      background: 'linear-gradient(to top, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0) 100%)',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span>📷 {activePoster.imageCaption}</span>
                      <span style={{ fontSize: '0.68rem', backgroundColor: 'rgba(255,255,255,0.22)', padding: '0.1rem 0.45rem', borderRadius: '4px', backdropFilter: 'blur(4px)' }}>
                        Production Ready
                      </span>
                    </div>
                  </div>
                )}
                {/* Poster Graphic 1: Full-Stack Architecture */}
                {activePoster.visualType === 'code_matrix' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.8rem', marginBottom: '1.2rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>System Architecture Map</span>
                      <span className="pill-badge pill-blue" style={{ fontSize: '0.7rem' }}>High-Throughput</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '1.2rem' }}>
                      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>FRONTEND LAYER</div>
                        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1d4ed8' }}>React / Next.js</div>
                        <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.2rem' }}>SSR • Hydrated UI • Responsive</div>
                      </div>
                      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>API &amp; MICROSERVICES</div>
                        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0284c7' }}>Node &amp; Python</div>
                        <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.2rem' }}>GraphQL • REST • Async Workers</div>
                      </div>
                      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>DATA PERSISTENCE</div>
                        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>Postgres &amp; Redis</div>
                        <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.2rem' }}>Read Replicas • In-Memory Cache</div>
                      </div>
                      <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>INFRASTRUCTURE</div>
                        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#059669' }}>AWS &amp; GCP Cloud</div>
                        <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.2rem' }}>Docker Containers • CI/CD</div>
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#0f172a', padding: '0.9rem', borderRadius: '10px', color: '#93c5fd', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      $ curl -X POST https://api.gurusworkshop.com/v1/deploy
                      <div style={{ color: '#10b981' }}>&gt; Build passed (0 warnings) &bull; CDN cache primed &bull; Ready</div>
                    </div>
                  </div>
                )}

                {/* Poster Graphic 2: Agentic AI Graph */}
                {activePoster.visualType === 'agentic_graph' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.8rem', marginBottom: '1.2rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>Autonomous Agent Loop</span>
                      <span className="pill-badge pill-emerald" style={{ fontSize: '0.7rem' }}>Self-Correcting</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', marginBottom: '1.2rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', backgroundColor: '#f0fdf4', padding: '0.75rem', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                        <span style={{ backgroundColor: '#059669', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>1</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#065f46' }}>Planner Agent</div>
                          <div style={{ fontSize: '0.78rem', color: '#047857' }}>Decomposes goal into structured dependency graph</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', backgroundColor: '#eff6ff', padding: '0.75rem', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                        <span style={{ backgroundColor: '#1d4ed8', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>2</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e40af' }}>Tool Executor Subagents</div>
                          <div style={{ fontSize: '0.78rem', color: '#1d4ed8' }}>Invokes database SQL, external APIs, and document parsers</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', backgroundColor: '#faf5ff', padding: '0.75rem', borderRadius: '10px', border: '1px solid #e9d5ff' }}>
                        <span style={{ backgroundColor: '#7c3aed', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>3</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#5b21b6' }}>Critic &amp; Verification Node</div>
                          <div style={{ fontSize: '0.78rem', color: '#6d28d9' }}>Enforces schema compliance and verifies result truthfulness</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '0.6rem' }}>
                      <span>Latency: 240ms</span>
                      <span style={{ color: '#059669', fontWeight: 700 }}>● Active Autonomy</span>
                    </div>
                  </div>
                )}

                {/* Poster Graphic 3: Chatbot UI Simulation */}
                {activePoster.visualType === 'chat_ui' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.8rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#059669' }}></div>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Enterprise Support Assistant</span>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Grounded on Internal Data</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div style={{ alignSelf: 'flex-end', backgroundColor: '#7c3aed', color: '#ffffff', padding: '0.65rem 1rem', borderRadius: '14px 14px 2px 14px', fontSize: '0.85rem', maxWidth: '85%' }}>
                        Can you summarize our Q3 SLA report and highlight any incidents in the auth service?
                      </div>
                      <div style={{ alignSelf: 'flex-start', backgroundColor: '#f1f5f9', color: '#0f172a', padding: '0.75rem 1rem', borderRadius: '14px 14px 14px 2px', fontSize: '0.85rem', maxWidth: '90%', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.2rem', color: '#1d4ed8' }}>Workshop AI Engine:</div>
                        Q3 auth service uptime achieved <strong>99.994%</strong>. Zero critical P1 outages occurred. 2 minor latency spikes (&lt;45s) were automatically resolved via container scaling.
                        <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#64748b' }}>
                          📄 Sources: <u>cloudwatch_q3_report.pdf</u> &bull; <u>sla_metrics.json</u>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input 
                        type="text" 
                        readOnly 
                        value="Ask a question about your enterprise codebase..." 
                        style={{ flex: 1, padding: '0.5rem 0.8rem', fontSize: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#94a3b8' }}
                      />
                      <button style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', padding: '0 0.8rem', fontSize: '0.85rem' }}>↑</button>
                    </div>
                  </div>
                )}

                {/* Poster Graphic 4: Mobile Device Mockup */}
                {activePoster.visualType === 'mobile_device' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.8rem', marginBottom: '1.2rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>Mobile Platform Specs</span>
                      <span className="pill-badge pill-blue" style={{ fontSize: '0.7rem' }}>iOS &amp; Android</span>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', borderRadius: '14px', padding: '1.2rem', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 800 }}>Native Performance Benchmark</span>
                        <span style={{ fontSize: '0.78rem', color: '#059669', fontWeight: 700 }}>60 FPS V-Sync</span>
                      </div>
                      <div style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '0.8rem' }}>
                        <div style={{ width: '96%', height: '100%', backgroundColor: '#0284c7' }}></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', fontSize: '0.82rem' }}>
                        <div>📱 <strong>SwiftUI / Native:</strong> Apple Store Top Tier</div>
                        <div>⚡ <strong>Cold Launch:</strong> &lt; 400ms</div>
                        <div>🔄 <strong>Offline Sync:</strong> SQLite / Realm</div>
                        <div>🔒 <strong>Security:</strong> FaceID / Biometric</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem', backgroundColor: '#eff6ff', borderRadius: '10px', fontSize: '0.82rem', color: '#1d4ed8' }}>
                      <span>🚀 Direct App Store &amp; Play Store Deployment</span>
                      <span style={{ fontWeight: 700 }}>Ready →</span>
                    </div>
                  </div>
                )}

                {/* Poster Graphic 5: DevOps Cluster Dashboard */}
                {activePoster.visualType === 'devops_cluster' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.8rem', marginBottom: '1.2rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>24/7 SLA Telemetry</span>
                      <span className="pill-badge pill-emerald" style={{ fontSize: '0.7rem' }}>High Availability (Per SOW)</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginBottom: '1.2rem' }}>
                       <div style={{ backgroundColor: '#f8fafc', padding: '0.9rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b' }}>UPTIME TARGET</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#059669' }}>SOW SLA</div>
                        <div style={{ fontSize: '0.75rem', color: '#10b981' }}>● High availability target</div>
                      </div>
                      <div style={{ backgroundColor: '#f8fafc', padding: '0.9rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b' }}>RESPONSE TIME</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#d97706' }}>&lt; 15 mins</div>
                        <div style={{ fontSize: '0.75rem', color: '#475569' }}>Dedicated SRE Pager</div>
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.8rem', fontSize: '0.82rem', color: '#92400e' }}>
                      🛡️ Automated dependency vulnerability remediation active. Weekly backup snapshots verified with simulated recovery.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Slider Navigation Arrows and Dots */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {posters.map((_, dotIdx) => (
                <button
                  key={dotIdx}
                  type="button"
                  onClick={() => setCurrentSlide(dotIdx)}
                  style={{
                    width: currentSlide === dotIdx ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: currentSlide === dotIdx ? activePoster.badgeColor : '#cbd5e1',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    padding: 0
                  }}
                  title={`Go to slide ${dotIdx + 1}`}
                />
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                type="button"
                onClick={() => setCurrentSlide((prev) => (prev - 1 + posters.length) % posters.length)}
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 700
                }}
                title="Previous Poster"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => setCurrentSlide((prev) => (prev + 1) % posters.length)}
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 700
                }}
                title="Next Poster"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceRollingPoster;
